import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { DatePicker as KendoDatePicker, MultiViewCalendar } from "@progress/kendo-react-dateinputs";
import { Component, DATE_FORMAT, ErrorMessages } from "utils/constants";
import { useStyles } from "./datepicker.style";
import $ from "jquery";

interface DatePickerProps {
  id: string;
  label?: string | null;
  labelId?: string;
  title: string;
  minDate?: Date;
  labelIcon?: React.ReactNode;
  className?: string;
  rootClass?: string;
  labelClassName?: string;
  selectedValue: Date;
  ariaLabelledBy: string;
  required: boolean;
  showMonthView?: boolean;
  error?: boolean;
  errorMessage?: string | null;
  onChange: (value: Date) => void;
  asteriskClassName?: string;
  onBlur?: (value: Date | null, inputValue: string) => void;
}
export const DatePicker: React.FC<DatePickerProps> = ({
  id,
  label,
  title,
  labelId,
  labelIcon,
  rootClass,
  labelClassName,
  ariaLabelledBy,
  minDate,
  error,
  errorMessage,
  required,
  showMonthView = false,
  onChange,
  selectedValue,
  asteriskClassName,
  onBlur: onDateBlur,
}: DatePickerProps): React.ReactElement => {
  const classes = useStyles();
  const formatPattern = "formatPattern";
  const calendarTitle = `${title}. Use Alt+Down Arrow to Open the popup and moves the focus to the displayed calendar.
  Use Alt+Up Arrow	to close the popup and moves the focus to the input element. Use Esc to close the popup and moves the focus to the input element.`;

  useEffect(() => {
    const dateInput = document.querySelector(`#${id}`);
    dateInput.setAttribute("role", "combobox");
    dateInput.removeAttribute("aria-valuetext");
    dateInput.removeAttribute("aria-valuemin");
    dateInput.removeAttribute("aria-valuemax");
    dateInput.removeAttribute("aria-valuenow");
  });

  // MutationObserver watches for DOM tree changes. When calendar popup is displayed, title and aria-label of calendar navigation buttons are updated based on the selected calendar view
  // Fixes accessibility bug ADR_23241-8 Interactive elements with inappropriate name: https://dev.azure.com/KPMG-KLS/LEAP/_workitems/edit/50501
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const calendarViewTitles = {
        "k-calendar-monthview": {
          prev: "Navigate to previous month",
          next: "Navigate to next month",
        },
        "k-calendar-yearview": {
          prev: "Navigate to previous year",
          next: "Navigate to next year",
        },
        "k-calendar-decadeview": {
          prev: "Navigate to previous decade",
          next: "Navigate to next decade",
        },
        "k-calendar-centuryview": {
          prev: "Navigate to previous century",
          next: "Navigate to next century",
        },
      };

      let prevButtonTitle = "Navigate to previous view";
      let nextButtonTitle = "Navigate to next view";

      const calendarView = $(".k-calendar .k-calendar-view").get(0);

      if (calendarView) {
        const viewClass = Object.keys(calendarViewTitles).find((cls) =>
          calendarView.classList.contains(cls),
        );
        if (viewClass) {
          prevButtonTitle = calendarViewTitles[viewClass].prev;
          nextButtonTitle = calendarViewTitles[viewClass].next;
        }
      }

      const prevButton = document.querySelector(".k-nav-prev");
      if (prevButton) {
        prevButton.setAttribute("title", prevButtonTitle);
        prevButton.setAttribute("aria-label", prevButtonTitle);
      }
      const nextButton = document.querySelector(".k-nav-next");
      if (nextButton) {
        nextButton.setAttribute("title", nextButtonTitle);
        nextButton.setAttribute("aria-label", nextButtonTitle);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <Box className={rootClass}>
      {label && (
        <Typography className={labelClassName}>
          {labelIcon}
          <label id={labelId} htmlFor={id}>
            {label} {required && <span className={asteriskClassName}>*</span>}
          </label>
        </Typography>
      )}
      <KendoDatePicker
        id={id}
        title={calendarTitle}
        value={selectedValue}
        formatPlaceholder={formatPattern}
        format={showMonthView ? DATE_FORMAT.MMMMyyyy : DATE_FORMAT.ddMMyyyy}
        placeholder={showMonthView ? DATE_FORMAT.MMMMyyyy : DATE_FORMAT.ddMMyyyy}
        min={minDate}
        ariaLabelledBy={ariaLabelledBy}
        onBlur={(e: any) => {
          setTimeout(() => {
            const dateInput = document.querySelector(`#${id}`);
            if (dateInput) dateInput.removeAttribute("aria-valuetext");
          }, 200);
          setTimeout(() => {
            if (!document.activeElement?.closest(".k-popup")) {
              const inputValue =
                (document.querySelector(`#${id}`) as HTMLInputElement | null)?.value ?? "";
              onDateBlur?.(e.target?.value ?? null, inputValue);
            }
          }, 50);
        }}
        ariaDescribedBy={`${Component.ErrorMessageId}${id}`}
        calendar={(props) => (
          <MultiViewCalendar
            className={classes.calendar}
            min={minDate}
            views={1}
            bottomView={showMonthView ? "year" : "month"}
            ref={props._ref}
            value={props.value}
            onChange={props.onChange}
          />
        )}
        popupSettings={{ appendTo: document.body }}
        required={required}
        onChange={(e) => onChange(e.target.value)}
      />
      {((required && !selectedValue) || error) && (
        <div role="alert">
          <span id={`${Component.ErrorMessageId}${id}`} className={classes.errorText}>
            {errorMessage ||
              (label
                ? `${label.replace(Component.Star, "")} ${ErrorMessages.InputRequired}`
                : ErrorMessages.InputRequired)}
          </span>
        </div>
      )}
    </Box>
  );
};
